import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { clsx } from 'clsx'

/**
 * Button component variants
 * - primary: Main action buttons (Wisteria Blue)
 * - secondary: Secondary actions
 * - danger: Destructive actions (red)
 * - ghost: Minimal styling for less prominent actions
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'

/**
 * Button size variants
 * - sm: Small buttons for compact UIs
 * - md: Default size
 * - lg: Large buttons for prominent actions
 */
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: ButtonVariant
  /** Size variant */
  size?: ButtonSize
  /** Loading state - shows spinner and disables button */
  isLoading?: boolean
  /** Loading text to display instead of children */
  loadingText?: string
  /** Icon to display before button text */
  leftIcon?: ReactNode
  /** Icon to display after button text */
  rightIcon?: ReactNode
  /** Button content */
  children: ReactNode
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: clsx(
    'bg-primary-solid text-primary-foreground',
    'hover:brightness-90 active:brightness-80',
    'focus-visible:ring-ring',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
  secondary: clsx(
    'bg-accent text-foreground',
    'hover:bg-accent/80 active:bg-accent/70',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
  danger: clsx(
    'bg-red-500 text-white',
    'hover:bg-red-600 active:bg-red-700',
    'focus-visible:ring-red-500',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
  ghost: clsx(
    'text-muted-foreground bg-transparent',
    'hover:bg-accent active:bg-accent/80',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  ),
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-base',
  lg: 'px-4 py-3 text-lg',
}

/**
 * Loading spinner component for button loading state
 */
function LoadingSpinner({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className={clsx('animate-spin', className)}
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Button component following elBooks UI/UX standards
 *
 * @example
 * // Primary button
 * <Button variant="primary">Save Entry</Button>
 *
 * @example
 * // Loading state
 * <Button variant="primary" isLoading loadingText="Saving...">Save Entry</Button>
 *
 * @example
 * // Danger button for destructive actions
 * <Button variant="danger">Void Entry</Button>
 *
 * @example
 * // With icons
 * <Button variant="primary" leftIcon={<PlusIcon />}>Add Entry</Button>
 */
interface ButtonContentProps {
  isLoading: boolean
  loadingText?: React.ReactNode
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  children: React.ReactNode
}

function ButtonContent({ isLoading, loadingText, leftIcon, rightIcon, children }: ButtonContentProps): JSX.Element {
  if (isLoading) {
    return (
      <>
        <LoadingSpinner className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">{loadingText ?? children}</span>
      </>
    )
  }
  return (
    <>
      {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </>
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      disabled,
      className,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled ?? isLoading
    return (
      <button
        ref={ref}
        aria-busy={isLoading}
        aria-disabled={isDisabled}
        className={clsx(
          'inline-flex items-center justify-center gap-2',
          'rounded-md font-medium',
          'whitespace-nowrap flex-shrink-0',
          '[&>svg]:flex-shrink-0',
          'transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        disabled={isDisabled}
        type={type}
        {...props}
      >
        <ButtonContent isLoading={isLoading} leftIcon={leftIcon} loadingText={loadingText} rightIcon={rightIcon}>
          {children}
        </ButtonContent>
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
